import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PlusIcon, TicketIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTripBookingDocument } from '../../../api/trip-bookings/create-trip-booking-document'
import { updateTripBookingDocument } from '../../../api/trip-bookings/update-trip-booking-document'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { transportTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripBookingDocument } from '../../../lib/types/models/trip-booking/trip-booking-document'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import TripBookingDocumentForm from './trip-booking-document-form'

type Props = {
  tripBookingId: number
}

export const tripBookingDocumentColumns: Column<TripBookingDocument>[] = [
  {
    accessorKey: 'name',
    header: 'Nom'
  },
  {
    accessorKey: 'directionality',
    header: 'Type',
    cell: ({ row }) => transportTypeMapping[row.original.directionality].label
  },
  {
    accessorKey: 'file',
    header: 'Billet',
    cell: ({ row }) => (
      <Badge
        onClick={() => window.open(row.original.file.path, '_blank')}
        variant="blue"
        icon={<TicketIcon width="24px" height="24px" color={tailwindColors.blue.DEFAULT} />}
      />
    )
  }
]

const TripBookingDocumentsDataTable = ({ tripBookingId }: Props) => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TripBookingDocument
  >(['create', 'edit'])
  const tableRef = React.useRef<DynamicTableRef<TripBookingDocument>>(null)

  const { mutateAsync: createMutation } = useMutation({
    mutationFn: createTripBookingDocument,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Billet créé avec succès')
    }
  })

  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: updateTripBookingDocument,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Billet modifié avec succès')
    }
  })

  return (
    <div>
      <NestedDataTable<TripBookingDocument, null>
        ref={tableRef}
        title={'Billets'}
        variant="action"
        deleteMessage={{
          name: 'Billet',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce billet ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces billets ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les billets ?'
        }}
        columns={tripBookingDocumentColumns}
        onEditAction={(document) => toggleModal('edit', document)}
        endpoint={API['trip-bookings'].getDocuments.url.replace(':id', tripBookingId.toString())}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un billet',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un billet'}
        icon={TicketIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <TripBookingDocumentForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => createMutation({ tripBookingId, ...values })
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier un billet'}
          icon={TicketIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <TripBookingDocumentForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: async (values) =>
                updateMutation({ id: selectedData.id, tripBookingId, ...values })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripBookingDocumentsDataTable
